import "../global";
import { Logger } from "../utils/Logger";
import { CookiesNames } from "../gtm/Cookies";
import { getCookie } from "typescript-cookie";
import { deleteCookieForDomain, setOneYearCookie } from "../utils/Cookies";
import { inject, injectable, singleton } from "tsyringe";
import { UserDataLayerAdapter } from "./UserDataLayerAdapter";
import { ServiceTokens } from "./ServiceTokens";
import { UserDataLayerAdapterFactory } from "./UserDataLayerAdapterFactory";
import { getUserAdapter } from "../gtm/GtmUtils";
import { UieClient } from "../cbcUie/UieClient";
import CaspianUserIdentifiersApiClient from "../caspian/userIdentifiers/CaspianUserIdentifiersApiClient";

/**
 * This class is used to coordinate the collection of user identifiers and send them to CBC's UIE and
 * Radio-Canada's Caspian API.
 */
@singleton()
@injectable()
export class UserIdExchange {
  constructor(
    @inject(CaspianUserIdentifiersApiClient) private caspianUIDsApiClient: CaspianUserIdentifiersApiClient,
    @inject(UieClient) private uieClient: UieClient,
    @inject(ServiceTokens.UserDataLayerAdapterFactory) private adapterFactory: UserDataLayerAdapterFactory,
  ) {}

  /**
   * LotameId of the current user as reported by Lotame's script
   @private
   * */
  private activeLotameId: string | undefined;

  /**
   * Set the rc_plus or rc_visitor identifier on the current user in the dataLayer
   * by calling the UIE system.
   * @param userAdapter The current user if already initialized. Undefined otherwise.
   */
  public async initUserFromUIE(userAdapter?: UserDataLayerAdapter): Promise<void> {
    if (userAdapter?.rcId) {
      await this.handleUserLoggedIn(userAdapter);
    } else {
      userAdapter = userAdapter ?? this.adapterFactory.getUserDataAdapter({});
      await this.uieClient.handleRcVisitorId();
    }

    this.setPPIDCookieIfNotSet(userAdapter);

    dataLayerQueue.push({
      event: `gtmLib.initUserFromUIE`,
      user: userAdapter.dataLayerUser,
    });
    if (userAdapter?.rcId) {
      Logger.log("User logged in. RCID:", userAdapter.rcId);
    }
    await this.sendCollectedIds();
  }

  /**
   * Send Lotame's unique identifier for the current user to Caspian.
   * @remarks
   * If the current user is not ready, keeps the value aside until it's loaded.
   * @param lotameId Lotame's unique user identifier
   */
  public async setLotameIdInUie(lotameId: string): Promise<void> {
    if (!lotameId) {
      return;
    }
    // It can happen that the string "undefined" is passed to this method.
    if (lotameId === "undefined") {
      Logger.warn("Invoked setLotameIdInUie with the undefined string value.");
      return;
    }

    this.activeLotameId = lotameId;

    // Do not modify. Will break tests otherwise
    Logger.verbose("User LotameID:", lotameId);
    await this.sendCollectedIds();
  }

  /**
   * Handle user initialization when the user is authenticated.
   * @param userAdapter The current user in the DataLayer
   * @private
   */
  private async handleUserLoggedIn(userAdapter: UserDataLayerAdapter): Promise<void> {
    let rcPlusId = getCookie(CookiesNames.rcPlus);

    // This ID is associated with a few MB of lotameIds because it is associated with the "undefined" loginradius_id
    // and should therefore be ignored!
    if (rcPlusId === "db9c3135-cb3d-4c24-9091-a5794949ea4a") {
      deleteCookieForDomain(CookiesNames.rcPlus);
      rcPlusId = undefined;
    }
    if (!rcPlusId) {
      rcPlusId = await this.uieClient.fetchRcPlusId(userAdapter);
      if (!rcPlusId) {
        return;
      } else {
        userAdapter.rc_plus_id = rcPlusId;
      }
    } else {
      userAdapter.rc_plus_id = rcPlusId;
    }
  }

  private async sendCollectedIds(): Promise<void> {
    // Wait until lotame's script has loaded and a user identifier has been resolved.
    if (!this.activeLotameId) {
      return;
    }

    const userAdapter = getUserAdapter();
    if (userAdapter && (userAdapter.rcId || userAdapter.rc_visitor_id)) {
      await this.caspianUIDsApiClient.sendUserIdentifiers(userAdapter, this.activeLotameId);
    }
  }

  /**
   * Sets the PPID cookie if it does not exist, or updates it if the value differs from the user's RCID.
   * @remarks
   * If the PPID cookie already exists, it will only be updated if its value is different from the user's RCID.
   * @param userAdapter The initialized user.
   * @return
   * Returns the PPID as set in the cookie.
   * @see {@link https://cbcradiocanada.atlassian.net/wiki/spaces/MNISD/pages/2328166974/PPID+publisher+provided+identifiers}
   */
  private setPPIDCookieIfNotSet(userAdapter: UserDataLayerAdapter): string | undefined {
    const currentValue = getCookie(CookiesNames.rcPPId);

    if (currentValue) {
      return this.handleExistingCookie(userAdapter, currentValue);
    }
    return this.handleNewCookie(userAdapter);
  }

  /**
   * Handles the case where the PPID cookie already exists.
   * @param userAdapter The initialized user.
   * @param currentValue The current value of the PPID cookie.
   * @private
   */
  private handleExistingCookie(userAdapter: UserDataLayerAdapter, currentValue: string): string {
    if (userAdapter.rcId && currentValue !== userAdapter.rcId) {
      return this.updateCookieAndUserAdapterPPID(userAdapter, userAdapter.rcId);
    }

    return this.updateCookieAndUserAdapterPPID(userAdapter, currentValue);
  }

  /**
   * Handles the case where the PPID cookie does not exist.
   * @param userAdapter The initialized user.
   * @private
   */
  private handleNewCookie(userAdapter: UserDataLayerAdapter): string | undefined {
    const ppId = userAdapter.rcId || userAdapter.rc_visitor_id;

    if (ppId) {
      this.updateCookieAndUserAdapterPPID(userAdapter, ppId);
      return ppId;
    }

    Logger.warn("Could not determine a PPID");
    userAdapter.ppid = undefined;
    return undefined;
  }

  /**
   * Updates the PPID cookie and the user adapter with the PPID value.
   * @param userAdapter The initialized user.
   * @param ppId The new PPID value.
   * @private
   */
  private updateCookieAndUserAdapterPPID(userAdapter: UserDataLayerAdapter, ppId: string): string {
    Logger.log("Updating PPID cookie:", ppId);
    setOneYearCookie(CookiesNames.rcPPId, ppId);
    userAdapter.ppid = ppId;
    return ppId;
  }
}
